// React
import React, { useEffect, useState } from 'react';

// Store
import useSiteStore from '../../../store/siteStore.js';

// Components 
import { EditOutlined, NavigateBeforeOutlined, NavigateNextOutlined, SendOutlined, ArrowBack } from '@mui/icons-material';
import { Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import WeeklyScheduleTable from '../../custom/WeeklyScheduleTable.js';
import CoachList from '../../custom/CoachList.js';
import ParentList from '../../custom/ParentList.js';
import CreateWeeklyScheduleSessions from '../../custom/CreateWeeklyScheduleSessions.js';
import CustomSnackbar from '../../custom/CustomSnackbar.js';

// Style
import { orange, grey, blue, green } from '@mui/material/colors/index.js';
import styles from '../Coach/coachHome.module.css';
import { darkTheme } from '../../../styles/colorSchemes/index.js';

// Hooks
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Other
import { getWeek, formatCalendarWeekString } from '../../../utils/index.js';

const AthleteHome = (props) => {
  const { addTrainingSession, athlete, coaches, handleChangeTrainingSession, handleSend, isAbove18, openAndCloseAll, openAndCloseTableRow, parents, prepCreateWeeklySchedule, startWeek,goToWochenplan } 
		= props;
  const { changeCalenderWeek, failMessage, highlightCurrentCalenderWeek, selectedCalenderWeek, selectedTab, setSelectedCalenderWeek, 
		setSnackbarOpenFail, snackbarOpenFail, year, setYear } = useSiteStore();
  const { isDarkMode } = useAppTheme();

	const [validWeekNumber, setValidWeekNumber] = useState(selectedCalenderWeek);
	const [displayWeekString, setDisplayWeekString] = useState('');

	// Dark Mode Helper Logic for week highlighting
	const currentDate = new Date();
	const currentWeek = getWeek(currentDate);
	const currentYear = currentDate.getFullYear();
	
	const isFuture = year > currentYear || (year === currentYear && validWeekNumber > currentWeek);
	const isPast = year < currentYear || (year === currentYear && validWeekNumber < currentWeek);
	const isCurrent = year === currentYear && validWeekNumber === currentWeek;

	// Ensure selectedCalenderWeek is valid, otherwise use current week
	useEffect(() => {
		const currentWeek = getWeek(new Date());
		if (!selectedCalenderWeek || isNaN(selectedCalenderWeek)) {
			setSelectedCalenderWeek(currentWeek);
			setValidWeekNumber(currentWeek);
		} else {
			setValidWeekNumber(selectedCalenderWeek);
		}
	}, [selectedCalenderWeek, setSelectedCalenderWeek]);

	// Update display week string when week or year changes
	useEffect(() => {
		if (validWeekNumber && year && !isNaN(validWeekNumber)) {
			const calculatedString = formatCalendarWeekString(validWeekNumber, year);
			setDisplayWeekString(calculatedString);
		}
	}, [validWeekNumber, year]);
  
	return (
		<Box sx={{ ml: '10px', mr: '10px' }}>
			{ selectedTab === 0 && (
			<>
				{/* Compact Navigation Row */}
				<Box sx={{ 
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: { xs: 2, md: 2 },
					mb: 2,
					mt: 1
				}}>
					{/* Left Spacer for centering the week selector */}
					<Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

					{/* Center: Week Navigation */}
					<Paper
						elevation={0}
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							border: '1px solid',
							borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
							borderRadius: '24px',
							overflow: 'hidden',
							bgcolor: isDarkMode ? darkTheme.cardBg : 'background.paper',
							transition: 'all 0.2s ease',
							width: { xs: '100%', md: 'auto' },
							maxWidth: { xs: '100%', md: 'none' },
							'&:hover': {
								boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
								borderColor: isDarkMode ? darkTheme.textSecondary : grey[400]
							}
						}}
					>
						{/* Previous Week Button */}
						<IconButton
							onClick={() => changeCalenderWeek(false)}
							sx={{
								borderRadius: 0,
								px: { xs: 1, sm: 1.5 },
								py: 0.75,
								color: isDarkMode 
									? (isFuture ? orange[500] : darkTheme.textSecondary)
									: highlightCurrentCalenderWeek(selectedCalenderWeek, "less"),
								transition: 'all 0.2s ease',
								'&:hover': {
									bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50],
									color: isDarkMode ? orange[500] : undefined
								}
							}}
						>
					<NavigateBeforeOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
				</IconButton>

				{/* Current Week Display - Click to go to today */}
						<Box
							onClick={() => {
								const currentDate = new Date();
								setYear(currentDate.getFullYear());
								setSelectedCalenderWeek(getWeek(currentDate));
							}}
							sx={{
								px: { xs: 2, sm: 3 },
								py: 0.75,
								cursor: 'pointer',
								transition: 'background-color 0.2s ease',
								flex: 1,
								minWidth: 0,
								'&:hover': {
									bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : blue[50]
								}
							}}
						>
							<Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" justifyContent="center">
								<Typography 
									variant="body2" 
									fontWeight={600}
									sx={{ 
										color: isDarkMode 
											? (isCurrent ? orange[500] : darkTheme.textPrimary)
											: highlightCurrentCalenderWeek(selectedCalenderWeek, "equal"),
										fontSize: { xs: '0.75rem', sm: '0.875rem' },
										whiteSpace: 'nowrap'
									}}
								>
									KW {validWeekNumber}
								</Typography>
								<Typography 
									variant="body2" 
									sx={{ display: { xs: 'none', sm: 'block' }, color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}
								>
									•
								</Typography>
								<Typography 
									variant="body2" 
									sx={{
										fontSize: { xs: '0.75rem', sm: '0.875rem' },
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										color: isDarkMode ? darkTheme.textSecondary : 'text.secondary'
									}}
								>
									{displayWeekString}
								</Typography>
							</Stack>
					</Box>

					{/* Next Week Button */}
					<IconButton
							onClick={() => changeCalenderWeek(true)}
							sx={{
								borderRadius: 0,
								px: { xs: 1, sm: 1.5 },
								py: 0.75,
								color: isDarkMode 
									? (isPast ? orange[500] : darkTheme.textSecondary)
									: highlightCurrentCalenderWeek(selectedCalenderWeek, "more"),
								transition: 'all 0.2s ease',
								'&:hover': {
									bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50],
									color: isDarkMode ? orange[500] : undefined
								}
							}}
						>
						<NavigateNextOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
					</IconButton>
				</Paper>

				{/* Right: Create Button container */}
				<Box sx={{ 
					flex: 1, 
					display: { xs: 'none', md: 'flex' },
					justifyContent: 'flex-end'
				}}>
					{isAbove18 && (
						<Button
							variant="contained"
							startIcon={<EditOutlined />}
							onClick={() => prepCreateWeeklySchedule()}
							sx={{
								bgcolor: isDarkMode ? darkTheme.accent : blue[600],
								color: isDarkMode ? '#1a1a1a' : 'white',
								textTransform: 'none',
								fontWeight: 600,
								px: 2.5,
								py: 1,
								borderRadius: 3,
								boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',								mr: 2.5,
								'&:hover': {
									bgcolor: isDarkMode ? orange[600] : blue[700],
									boxShadow: isDarkMode ? '0 4px 14px rgba(255, 152, 0, 0.5)' : '0 4px 14px rgba(25, 118, 210, 0.4)'
								}
							}}
						>
							Trainingseinheit erstellen
						</Button>
					)}
				</Box>
				{/* Mobile: Create Button (only if above 18) */}
				{isAbove18 && (
					<Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', width: '100%' }}>
						<Button
							variant="contained"
							startIcon={<EditOutlined />}
							onClick={() => prepCreateWeeklySchedule()}
							sx={{
								bgcolor: isDarkMode ? darkTheme.accent : blue[600],
								color: isDarkMode ? '#1a1a1a' : 'white',
								textTransform: 'none',
								fontWeight: 600,
								px: 2.5,
								py: 1,
								borderRadius: 3,
								boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
								'&:hover': {
								bgcolor: isDarkMode ? orange[600] : blue[700],
								boxShadow: isDarkMode ? '0 4px 14px rgba(255, 152, 0, 0.5)' : '0 4px 14px rgba(25, 118, 210, 0.4)'
							}
							}}
						>
							Trainingseinheit erstellen
						</Button>
					</Box>
				)}
			</Box>				<WeeklyScheduleTable
					isFeedbackEditable= { true }
					isEditable={ false }
					openAndCloseTableRow={ openAndCloseTableRow }
					openAndCloseAll={ openAndCloseAll }
					showFeedbackButton={ true }
					selectedAthlete={ athlete }
					isAthlete
					isDeletable={ isAbove18 }/>
		</>
		)}

		{ selectedTab === 1 && (
			<CoachList search={''} coaches={ coaches } showAthleteAmount={ false } isEditable={ false }/>
		)}

		{ selectedTab === 2 && (
			<ParentList search={''} parents={ parents } showAthleteAmount={ false } isEditable={ false }/>
		)}

		{ selectedTab === 3 && (
			<>
				{/* Navigation Row with Week Selector and Action Buttons */}
				<Box sx={{ 
					display: 'flex', 
					flexDirection: { xs: 'column', md: 'row' },
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: { xs: 2, md: 2 },
					mb: 2,
					mt: 1,
					px: 1
				}}>
					{/* Left Spacer for centering the week selector */}
					<Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
					{/* Center: Week Navigation */}
					<Paper
						elevation={0}
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							border: '1px solid',
						borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
						borderRadius: '24px',
						overflow: 'hidden',
						bgcolor: isDarkMode ? darkTheme.cardBg : 'background.paper',
						transition: 'all 0.2s ease',
						width: { xs: '100%', md: 'auto' },
						maxWidth: { xs: '100%', md: 'none' },
						'&:hover': {
							boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
							borderColor: isDarkMode ? darkTheme.textSecondary : grey[400]
						}
					}}
				>
					{/* Previous Week Button */}
					<IconButton
						onClick={() => changeCalenderWeek(false)}
						sx={{
							borderRadius: 0,
							px: { xs: 1, sm: 1.5 },
							py: 0.75,
							color: isDarkMode 
								? (isFuture ? orange[500] : darkTheme.textSecondary)
								: highlightCurrentCalenderWeek(selectedCalenderWeek, "less"),
							transition: 'all 0.2s ease',
							'&:hover': {
								bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50],
								color: isDarkMode ? orange[500] : undefined
							}
						}}
					>
						<NavigateBeforeOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
					</IconButton>

					{/* Current Week Display - Click to go to today */}
					<Box
						onClick={() => {
							const currentDate = new Date();
							setYear(currentDate.getFullYear());
							setSelectedCalenderWeek(getWeek(currentDate));
						}}
						sx={{
							px: { xs: 2, sm: 3 },
							py: 0.75,
							cursor: 'pointer',
							transition: 'background-color 0.2s ease',
							flex: 1,
							minWidth: 0,
							'&:hover': {
								bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : blue[50]
							}
						}}
					>
						<Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" justifyContent="center">
							<Typography 
								variant="body2" 
								fontWeight={600}
								sx={{ 
									color: isDarkMode 
										? (isCurrent ? orange[500] : darkTheme.textPrimary)
										: highlightCurrentCalenderWeek(selectedCalenderWeek, "equal"),
									fontSize: { xs: '0.75rem', sm: '0.875rem' },
									whiteSpace: 'nowrap'
								}}
							>
								KW {validWeekNumber}
							</Typography>
							<Typography 
								variant="body2" 
								sx={{ display: { xs: 'none', sm: 'block' }, color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}
							>
								•
							</Typography>
							<Typography 
								variant="body2" 
								sx={{
									fontSize: { xs: '0.75rem', sm: '0.875rem' },
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									color: isDarkMode ? darkTheme.textSecondary : 'text.secondary'
								}}
							>
								{displayWeekString}
							</Typography>
						</Stack>
					</Box>

					{/* Next Week Button */}
					<IconButton
						onClick={() => changeCalenderWeek(true)}
						sx={{
							borderRadius: 0,
							px: { xs: 1, sm: 1.5 },
							py: 0.75,
							color: isDarkMode 
								? (isPast ? orange[500] : darkTheme.textSecondary)
								: highlightCurrentCalenderWeek(selectedCalenderWeek, "more"),
							transition: 'all 0.2s ease',
							'&:hover': {
								bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50],
								color: isDarkMode ? orange[500] : undefined							}
						}}
					>
						<NavigateNextOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
					</IconButton>
				</Paper>

				{/* Right: Action Buttons */}
				<Stack 						direction="row" 
						spacing={1.5} 
						sx={{ 
							flex: 1,
							width: { xs: '100%', md: 'auto' },
							justifyContent: { xs: 'center', md: 'flex-end' }
						}}
					>
				{/* Back Button */}
				<Button
					variant="outlined"
					startIcon={<ArrowBack />}
					onClick={() => goToWochenplan()}
					sx={{
						textTransform: 'none',
						fontWeight: 500,
						borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
						color: isDarkMode ? darkTheme.textPrimary : 'text.primary',
						px: 2,
						py: 0.75,
						borderRadius: 3,
						'&:hover': {
							bgcolor: isDarkMode ? darkTheme.cardHover : grey[100],
							borderColor: isDarkMode ? darkTheme.textSecondary : grey[400]
						}
					}}
				>
					Zurück
				</Button>

				{/* Save Button */}
				<Button
					variant="contained"
					startIcon={<SendOutlined />}
					onClick={() => handleSend()}
					sx={{
								bgcolor: green[600],
								color: 'white',
								textTransform: 'none',
								fontWeight: 600,
								px: { xs: 1.5, sm: 2.5 },
								py: { xs: 0.75, sm: 1 },
								borderRadius: 3,
								boxShadow: 2,
								fontSize: { xs: '0.8rem', sm: '0.875rem' },
								'&:hover': {
									bgcolor: green[700],
									boxShadow: 4
								}
							}}
						>
							Plan speichern
						</Button>
					</Stack>
				</Box>

				<CustomSnackbar 
					open={snackbarOpenFail} 
					onClose={() => setSnackbarOpenFail(false)} 
					message={failMessage} 
					severity="error" 
					autoHideDuration={4000}
				/>
				
				<CreateWeeklyScheduleSessions styles={ styles } startWeek={ startWeek } year={ year } handleChangeTrainingSession={ handleChangeTrainingSession } 
					addTrainingSession={ addTrainingSession } isAthlete/>
			</>
		)}

	</Box >
	);
}

export default AthleteHome;